import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { ELensViewAreaType, LensIndexItem } from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  handleColorItemPriceInfo,
  handleLensIndexPriceInfo,
} from '@/app/_lens-components/utils';
import Image from '@/components/Image';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { add, subtract } from '@/utils/math';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { processLineBreaks } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';

export default function LensViewArea({
  progressiveViewArea,
  selectedLensIndexItem,
  selectedLensViewAreaType,
  onSelectedLensViewAreaType,
  onSelectedAdvancedLensIndexItem,
}: {
  progressiveViewArea: GetResponseType<
    ApiResponseDefinition['GET:/api/lens/view_area']
  >['view_area'];
  selectedLensIndexItem: LensIndexItem;
  selectedLensViewAreaType: ELensViewAreaType;
  onSelectedLensViewAreaType: (type: ELensViewAreaType) => void;
  onSelectedAdvancedLensIndexItem: (item: LensIndexItem) => void;
}) {
  const t = useTranslations('lens-process');
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const waiMode = useStore((state) => state.waiMode);
  const { trackEvent } = useReporting();
  const {
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    selectedLensType,
    selectedBrand,
    selectedColorItem,
    lensTypeForTracking,
  } = useLensProcessStore();

  const [swiperData, setSwiperData] = useState<
    Array<{
      type: string;
      recommend: number;
      progressive_name: string;
      img: string;
      description: string;
    }>
  >([]);
  const [diffPrice, setDiffPrice] = useState<string>('0.00');

  useEffect(() => {
    if (selectedLensIndexItem?.advanced) {
      const advancedPrice = handleLensIndexPriceInfo(
        selectedLensIndexItem.advanced,
        currency,
        userInfo,
      );
      const advancedTypePrice = handleColorItemPriceInfo(
        selectedLensIndexItem.advanced,
        false,
        currency,
        userInfo,
      );
      const normalPrice = handleLensIndexPriceInfo(selectedLensIndexItem, currency, userInfo);
      const normalTypePrice = handleColorItemPriceInfo(
        selectedColorItem,
        false,
        currency,
        userInfo,
      );
      const innerDiffPrice = subtract(advancedPrice.sell_price, normalPrice.sell_price);
      const innerTypeDiffPrice = subtract(advancedTypePrice.sell_price, normalTypePrice.sell_price);
      logger.warn('diff price', innerDiffPrice.toFixed(2), innerTypeDiffPrice.toFixed(2));
      setDiffPrice(add(innerDiffPrice, innerTypeDiffPrice).toFixed(2));
    }
  }, [selectedLensIndexItem, userInfo, currency, selectedColorItem]);

  // 计算高级渐进镜和普通渐进镜差价

  // 初始化数据
  useEffect(() => {
    if (progressiveViewArea) {
      buildDataAction();
    }
  }, [progressiveViewArea]);

  // 初始化轮播数据
  const buildDataAction = () => {
    const viewData = progressiveViewArea;

    const ret = [
      {
        type: ELensViewAreaType.NORMAL,
        recommend: 0, // 1： 推荐， 2：不推荐
        progressive_name: viewData.progressive_name,
        img: viewData.progressive_img_src,
        description: viewData.progressive_description,
      },
      {
        type: ELensViewAreaType.ADVANCED,
        recommend: viewData.recommend, // 1： 推荐， 2：不推荐
        progressive_name: viewData.advance_progressive_name,
        img: viewData.advance_progressive_img_src,
        description: viewData.advance_progressive_description,
      },
    ];

    // 根据排序设置数据
    setSwiperData(viewData.sort === 1 ? ret : ret.reverse());
  };

  // 处理项目点击
  const onItemClick = (item: any) => {
    // 如果点击的是已选中项，不做处理
    if (item.type === selectedLensViewAreaType) {
      return;
    }

    onSelectedAdvancedLensIndexItem(
      item.type === ELensViewAreaType.ADVANCED ? selectedLensIndexItem.advanced : null,
    );
    onSelectedLensViewAreaType(item.type);

    SDViewAreaPageSelectClick(item.type === ELensViewAreaType.ADVANCED);
  };

  useEffectWithCurReady(() => {
    // 埋点：页面浏览
    try {
      const { index_name } = selectedLensIndexItem;
      const { price: lensIndexPrice, sell_price: lensIndexSellPrice } = handleLensIndexPriceInfo(
        selectedLensIndexItem,
        currency,
        userInfo,
      );
      const { price, sell_price } = handleColorItemPriceInfo(
        selectedColorItem,
        false,
        currency,
        userInfo,
      );
      trackEvent(
        {
          eventName: 'ViewAreaPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            lens_type_name: selectedLensType?.lens_name,
            lens_type: lensTypeForTracking,
            lens_brand: selectedBrand?.brand_title,
            lens_brand_name: selectedBrand?.brand_name,
            lens_color: selectedColorItem?.color_name,
            lens_id: `${selectedLensIndexItem.id}`,
            lens_index: selectedLensIndexItem.refractive_index,
            lens_code: selectedLensIndexItem.code,
            lens_index_name: index_name,
            lens_price: parseFloat(price),
            current_lens_price: parseFloat(sell_price),
            lens_index_price: parseFloat(lensIndexPrice),
            current_lens_index_price: parseFloat(lensIndexSellPrice),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }, [userInfo]);

  function SDViewAreaPageSelectClick(isAdvanced: boolean) {
    try {
      const colorItem = isAdvanced ? selectedLensIndexItem.advanced : selectedColorItem;
      const lensIndexItem = isAdvanced ? selectedLensIndexItem.advanced : selectedLensIndexItem;
      const lensIndexPriceInfo = handleLensIndexPriceInfo(lensIndexItem, currency, userInfo);
      const colorItemPriceInfo = handleColorItemPriceInfo(colorItem, false, currency, userInfo);

      trackEvent(
        {
          eventName: 'ViewAreaPage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            lens_id: `${lensIndexItem.id}`,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
            lens_index_name: lensIndexItem.index_name,
            lens_index: lensIndexItem.refractive_index,
            lens_code: lensIndexItem.code,
            lens_index_price: parseFloat(lensIndexPriceInfo.price),
            current_lens_index_price: parseFloat(lensIndexPriceInfo.sell_price),
            currency: currency.name,

            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            lens_type_name: selectedLensType?.lens_name,
            lens_type: lensTypeForTracking,
            lens_brand: selectedBrand?.brand_title,
            lens_brand_name: selectedBrand?.brand_name,
            lens_color: selectedColorItem?.color_name,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  // 渲染空视图
  const renderEmptyView = () => (
    <div className={stepCommonClassName.emptyState}>
      <Image src="/lens-process/null.png" width={118} height={118} alt={t('empty')} />
      <p className={stepCommonClassName.emptyStateText}>{t('sorryNoSuitableLensFound')}</p>
      <button className={stepCommonClassName.emptyStateButton}>{t('editPrescription')}</button>
    </div>
  );

  return (
    <div className="overflow-y-hidden h-full box-border font-br">
      <h3 className={stepCommonClassName.stepTitle}>
        <span>{progressiveViewArea?.visual_field_title}</span>
      </h3>

      {Array.isArray(swiperData) && swiperData.length > 0 ? (
        <ul className={stepCommonClassName.optionsContainer}>
          {swiperData.map((item, index) => (
            <li
              key={index}
              className={`${stepCommonClassName.optionItem} ${
                item.recommend === 1 ? stepCommonClassName.recommendedItem : ''
              }`}
            >
              {item.recommend === 1 && (
                <div className={stepCommonClassName.recommendBadge}>
                  <div className={stepCommonClassName.recommendBadgeContent}>
                    <span>{t('recommendedForYou')}</span>
                  </div>
                </div>
              )}

              <div
                className={`${stepCommonClassName.optionBox} ${
                  selectedLensViewAreaType === item.type
                    ? stepCommonClassName.selectedOptionBox
                    : ''
                }`}
                onClick={() => onItemClick(item)}
              >
                <div className={`${stepCommonClassName.optionContent} !items-center`}>
                  <div className={`${stepCommonClassName.imageContainer} min-w-[100px]`}>
                    <Image
                      src={item.img}
                      className="w-[100px] h-[100px] object-contain"
                      width={100}
                      height={100}
                      alt={item.progressive_name}
                    />
                  </div>

                  <div className={stepCommonClassName.textContainer}>
                    <div className={stepCommonClassName.textCenterContainer}>
                      <p className={stepCommonClassName.titleContainer}>{item.progressive_name}</p>
                      <p
                        className={stepCommonClassName.description(waiMode)}
                        dangerouslySetInnerHTML={{ __html: processLineBreaks(item.description) }}
                      />
                    </div>

                    <div className={stepCommonClassName.priceContainer}>
                      <span className={stepCommonClassName.priceText}>
                        {item.type === ELensViewAreaType.NORMAL
                          ? t('free')
                          : formatPriceNumberWithSymbol(diffPrice, currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        renderEmptyView()
      )}
    </div>
  );
}
